<template>
  <SnackbarAlert />
  <ErroConexao v-if="mostrarErroConexao" />
  <v-container
    v-else
    class="py-12"
  >
    <TitleHeader>Organograma</TitleHeader>

    <v-row
      class="w-100 max-width mx-auto"
      align="start"
      justify="center"
      no-gutters
    >
      <v-col
        cols="12"
        sm="12"
        md="11"
        lg="10"
        xl="9"
      >
        <v-card
          elevation="10"
          class="pa-6 rounded-xl bg-form-card border-left-institucional"
        >
          <template v-if="loading">
            <LoadingState text="Carregando..." />
          </template>

          <template v-else-if="erro">
            <p class="status-message error">{{ erro }}</p>
          </template>

          <template v-else-if="organograma.length">
            <div class="org-wrapper">
              <div
                v-for="(grupo, index) in organograma"
                :key="index"
                class="org-nivel"
              >
                <!-- Título (cargo) -->
                <div class="org-nodo org-nodo-pai">{{ grupo.titulo }}</div>

                <!-- Conectores se houver filhos -->
                <div
                  v-if="grupo.itens.length || grupo.extras"
                  class="org-conectores"
                >
                  <div class="linha-vertical"></div>
                  <div class="linha-horizontal"></div>
                </div>

                <!-- Filhos (nomes e extras) -->
                <div class="org-filhos">
                  <!-- Nomes principais -->
                  <div
                    v-for="(item, idx) in grupo.itens"
                    :key="'item-' + idx"
                    class="org-nodo"
                  >
                    {{ item }}
                  </div>

                  <!-- Extras (ex: [3], [4]...) -->
                  <template v-if="grupo.extras">
                    <div
                      v-for="(valores, chave) in grupo.extras"
                      :key="'extra-' + index + '-' + chave"
                    >
                      <div
                        v-for="(valor, i) in valores"
                        :key="'extra-' + index + '-' + chave + '-' + i"
                        class="org-nodo"
                      >
                        {{ valor }}
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="empty-state">
              <div class="empty-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6m7 1.5L18.5 9H13V3.5M8 13h8v2H8v-2m0 4h8v2H8v-2m0-8h5v2H8V9Z"
                  />
                </svg>
              </div>
              <h2 class="empty-title">Nenhum Organograma encontrado</h2>
              <p class="empty-description">Ainda não há registros disponíveis nesta seção.</p>
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useMainStore } from '@/stores/index'
import { useRuntimeConfig } from '#imports'
import { useRouter } from 'vue-router'
import TitleHeader from '@/components/TitleHeader.vue'
import ErroConexao from '@/components/ErroConexao.vue'
import LoadingState from '@/components/LoadingState.vue'

interface GrupoOrganograma {
  titulo: string
  itens: string[]
  extras?: Record<string, string[]>
}

interface PaginaOrganograma {
  descricao: string
}

definePageMeta({
  middleware: 'manutencao',
  title: 'FUNARPEN - Organograma',
})

useHead({
  title: 'Organograma',
  meta: [{ name: 'description', content: 'Organograma' }],
})

const config = useRuntimeConfig()
const baseUrl = config.public.backofficeApiBaseUrl
const router = useRouter()
const mainStore = useMainStore()
const resultado = ref<PaginaOrganograma | null>(null)
const loading = ref(true)
const erro = ref('')
const organograma = ref<GrupoOrganograma[]>([])
const mostrarErroConexao = ref(false)

function parseOrganogramaFromHtml(html: string): GrupoOrganograma[] {
  const grupos: GrupoOrganograma[] = []
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const elementos = Array.from(doc.querySelectorAll('p'))

  let grupoAtual: GrupoOrganograma | null = null

  for (const el of elementos) {
    const texto = el.textContent?.trim() || ''
    const match = texto.match(/^\[(\d+)\]\s*(.*)$/)
    if (!match) continue

    const chave = match[1]
    const valor = match[2].trim()

    if (chave === '1') {
      if (grupoAtual) grupos.push(grupoAtual)
      grupoAtual = { titulo: valor, itens: [] }
    } else if (chave === '2') {
      grupoAtual?.itens.push(valor)
    } else {
      if (!grupoAtual) continue
      if (!grupoAtual.extras) grupoAtual.extras = {}
      if (!grupoAtual.extras[chave]) grupoAtual.extras[chave] = []
      grupoAtual.extras[chave].push(valor)
    }
  }

  if (grupoAtual) grupos.push(grupoAtual)
  return grupos
}

function mostrarSnackbar(message: string, type: 'success' | 'error' = 'error') {
  mainStore.snackbar = {
    text: message,
    color: type === 'error' ? 'red-darken-2' : 'green-darken-2',
    variant: 'elevated',
    display: true,
    link: null,
  }
}

async function buscarPagina() {
  loading.value = true
  erro.value = ''
  mostrarErroConexao.value = false

  try {
    const res = await fetch(`${baseUrl}/portal/paginas/buscar-pagina?slug=funarpen-organograma`)

    if (res.status === 401) {
      router.push('/manutencao/acesso-negado')
      return
    }

    if (res.status === 404) {
      resultado.value = null
      return
    }

    if (!res.ok) throw new Error('Erro ao buscar dados')

    const data = await res.json()
    resultado.value = data && Object.keys(data).length > 0 ? data : null

    if (resultado.value?.descricao) {
      organograma.value = parseOrganogramaFromHtml(resultado.value.descricao)
    }
  } catch (e: any) {
    if (e?.response?.status === 401) {
      router.push('/manutencao/acesso-negado')
    } else if (e?.response?.status === 404) {
      mostrarSnackbar('Página não encontrada', 'error')
    } else if (e.message?.includes('Failed to fetch')) {
      mostrarSnackbar('Erro de conexão. Verifique sua internet.', 'error')
      mostrarErroConexao.value = true
    } else {
      mostrarSnackbar(e.message || 'Erro inesperado ao carregar os dados.', 'error')
      mostrarErroConexao.value = true
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  buscarPagina()
})

onBeforeUnmount(() => {
  mainStore.resetSnackbar()
})
</script>

<style scoped>
.bg-form-card {
  background-color: rgba(255, 255, 255, 0.92);
}

.border-left-institucional {
  border-left: 6px solid var(--funarpen-blue);
}

.org-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(2.5rem, 6vw, 4.5rem);
  padding: clamp(1.5rem, 4vw, 2.5rem) 1rem;
}

.org-nivel {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.org-nodo {
  font-size: 0.9rem;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 251, 253, 0.94)), #fff;
  border: 1px solid rgba(8, 47, 73, 0.14);
  padding: 1rem 1.35rem;
  border-radius: var(--funarpen-radius);
  font-weight: 800;
  color: var(--funarpen-blue);
  min-width: 200px;
  text-align: center;
  box-shadow: var(--funarpen-shadow-soft);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}

.org-nodo:hover {
  transform: translateY(-2px);
  box-shadow: var(--funarpen-shadow);
}

.org-nodo-pai {
  font-size: 1.1rem;
  background: linear-gradient(135deg, var(--funarpen-blue), var(--funarpen-blue-2));
  border-color: transparent;
  color: white;
}

.org-filhos {
  display: flex;
  gap: 2rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
}

.org-conectores {
  position: relative;
  width: 100%;
  height: 30px;
  margin-top: 0.5rem;
}

.linha-vertical {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-1px);
  width: 2px;
  height: 20px;
  background-color: rgba(191, 152, 81, 0.82);
  z-index: 1;
}

.linha-horizontal {
  position: absolute;
  top: 20px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: rgba(191, 152, 81, 0.82);
  z-index: 0;
}

@media (max-width: 768px) {
  .org-filhos {
    flex-direction: column;
    gap: 1rem;
  }

  .linha-horizontal {
    display: none;
  }

  .linha-vertical {
    left: 50%;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: rgba(8, 47, 73, 0.8);
}

.spinner {
  width: 64px;
  height: 64px;
  border: 6px solid rgba(8, 47, 73, 0.18);
  border-top-color: var(--funarpen-gold);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  font-weight: 500;
}

.empty-state {
  text-align: center;
  color: rgba(8, 47, 73, 0.85);
}

.empty-icon svg {
  width: 72px;
  height: 72px;
  fill: rgba(8, 47, 73, 0.58);
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.empty-description {
  font-size: 1.125rem;
  font-weight: 400;
  color: rgba(8, 47, 73, 0.72);
  line-height: 1.6;
}
</style>
